import { useMemo, useState } from "react"
import { Business, Contact, Household } from "@/schema"
import { BadgeComboboxSelect, Option } from "@ui/badge-input/combobox"
import InputLabel from "@ui/badge-input/input-label"
import { cn } from "@ui/utils"
import { Building2, Contact as ContactIcon, Home, X } from "lucide-react"

import { useFetchBusinesses } from "@/hooks/api/businesses"
import { useFetchContacts } from "@/hooks/api/contacts"
import { useFetchHouseholds } from "@/hooks/api/households"

const CONTACT_VALUE_PREFIX = "contact:"
const HOUSEHOLD_VALUE_PREFIX = "household:"
const BUSINESS_VALUE_PREFIX = "business:"
const CLEAR_VALUE = "clear-selection"

type RelationshipOptionData = Contact | Household | Business | null

export interface TaskCreateRelationshipSelectorProps {
  selectedHousehold: Household | null
  onHouseholdSelect: (household: Household | null) => void
  selectedContact: Contact | null
  onContactSelect: (contact: Contact | null) => void
  selectedBusiness: Business | null
  onBusinessSelect: (business: Business | null) => void
  allowHouseholds?: boolean
  allowContacts?: boolean
  allowBusinesses?: boolean
  householdType?: "prospect" | "client" | null
  disabled?: boolean
  className?: string
  bookId?: string
  variant?: "badge" | "field"
}

function getOptionValue(type: "contact" | "household" | "business", id: string) {
  if (type === "contact") return `${CONTACT_VALUE_PREFIX}${id}`
  if (type === "household") return `${HOUSEHOLD_VALUE_PREFIX}${id}`
  return `${BUSINESS_VALUE_PREFIX}${id}`
}

export default function TaskCreateRelationshipSelector({
  selectedHousehold,
  onHouseholdSelect,
  selectedContact,
  onContactSelect,
  selectedBusiness,
  onBusinessSelect,
  allowHouseholds = true,
  allowContacts = true,
  allowBusinesses = true,
  householdType,
  disabled = false,
  className,
  bookId,
  variant = "badge",
}: TaskCreateRelationshipSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const isSearchActive = searchQuery.trim().length > 0
  const normalizedQuery = searchQuery.trim().toLowerCase()

  const contactsSearchTerm = allowContacts && isSearchActive ? searchQuery : undefined
  const { data: contactsData, isLoading: isFetchingContacts } = useFetchContacts(contactsSearchTerm, bookId)
  const { data: householdsData, isLoading: isFetchingHouseholds } = useFetchHouseholds(
    allowHouseholds ? searchQuery : undefined,
    householdType ?? undefined,
    bookId
  )
  const businessesSearchTerm = allowBusinesses && isSearchActive ? searchQuery : undefined
  const { data: businessesData, isLoading: isFetchingBusinesses } = useFetchBusinesses(businessesSearchTerm, bookId)

  const options = useMemo<Option<RelationshipOptionData>[]>(() => {
    const nextOptions: Option<RelationshipOptionData>[] = []

    const hasSelection = !!selectedContact || !!selectedHousehold || !!selectedBusiness

    if (hasSelection && !disabled) {
      nextOptions.push({
        value: CLEAR_VALUE,
        label: "Clear selection",
        icon: <X size={16} strokeWidth={1.5} />,
        data: null,
      })
    }

    const selectedHouseholdOption = selectedHousehold
      ? {
          value: getOptionValue("household", selectedHousehold.id),
          label: selectedHousehold.name || "",
          icon: <Home size={16} strokeWidth={1.5} />,
          data: selectedHousehold,
        }
      : null

    const selectedContactOption = selectedContact
      ? {
          value: getOptionValue("contact", selectedContact.id),
          label: selectedContact.person?.full_name || "",
          icon: <ContactIcon size={16} strokeWidth={1.5} />,
          data: selectedContact,
        }
      : null

    const selectedBusinessOption = selectedBusiness
      ? {
          value: getOptionValue("business", selectedBusiness.id),
          label: selectedBusiness.name || "",
          icon: <Building2 size={16} strokeWidth={1.5} />,
          data: selectedBusiness,
        }
      : null

    if (allowHouseholds) {
      let householdOptions =
        householdsData?.households?.map<Option<Household>>((household) => ({
          value: getOptionValue("household", household.id),
          label: household.name || "",
          icon: <Home size={16} strokeWidth={1.5} />,
          data: household,
        })) ?? []

      if (isSearchActive && normalizedQuery) {
        householdOptions = householdOptions.filter((option) => option.label.toLowerCase().includes(normalizedQuery))
      }

      if (
        selectedHouseholdOption &&
        !householdOptions.find((option) => option.value === selectedHouseholdOption.value)
      ) {
        nextOptions.push(selectedHouseholdOption)
      }

      nextOptions.push(...householdOptions)
    }

    if (allowContacts) {
      let contactOptions =
        contactsData?.contacts?.map<Option<Contact>>((contact) => ({
          value: getOptionValue("contact", contact.id),
          label: contact.person?.full_name || "",
          icon: <ContactIcon size={16} strokeWidth={1.5} />,
          data: contact,
        })) ?? []

      if (isSearchActive && normalizedQuery) {
        contactOptions = contactOptions.filter((option) => option.label.toLowerCase().includes(normalizedQuery))
      }

      const hasSelectedContactInResults = contactOptions.some((option) => option.value === selectedContactOption?.value)

      if (selectedContactOption && !hasSelectedContactInResults) {
        contactOptions.unshift(selectedContactOption)
      }

      nextOptions.push(...contactOptions)
    }

    if (allowBusinesses) {
      let businessOptions =
        businessesData?.businesses?.map<Option<Business>>((business) => ({
          value: getOptionValue("business", business.id),
          label: business.name || "",
          icon: <Building2 size={16} strokeWidth={1.5} />,
          data: business,
        })) ?? []

      if (isSearchActive && normalizedQuery) {
        businessOptions = businessOptions.filter((option) => option.label.toLowerCase().includes(normalizedQuery))
      }

      const hasSelectedBusinessInResults = businessOptions.some(
        (option) => option.value === selectedBusinessOption?.value
      )

      if (selectedBusinessOption && !hasSelectedBusinessInResults) {
        businessOptions.unshift(selectedBusinessOption)
      }

      nextOptions.push(...businessOptions)
    }

    return nextOptions
  }, [
    allowContacts,
    allowHouseholds,
    allowBusinesses,
    contactsData?.contacts,
    disabled,
    businessesData?.businesses,
    householdsData?.households,
    isSearchActive,
    normalizedQuery,
    selectedContact,
    selectedBusiness,
    selectedHousehold,
  ])

  const handleValueChange = (value: string | number) => {
    if (typeof value !== "string") return
    setSearchQuery("")

    if (value === CLEAR_VALUE) {
      onContactSelect(null)
      onHouseholdSelect(null)
      onBusinessSelect(null)
      return
    }

    if (value.startsWith(CONTACT_VALUE_PREFIX)) {
      const contactOption = options.find((option) => option.value === value)
      onContactSelect((contactOption?.data as Contact | undefined) ?? null)
      onHouseholdSelect(null)
      onBusinessSelect(null)
      return
    }

    if (value.startsWith(HOUSEHOLD_VALUE_PREFIX)) {
      const householdOption = options.find((option) => option.value === value)
      onHouseholdSelect((householdOption?.data as Household | undefined) ?? null)
      onContactSelect(null)
      onBusinessSelect(null)
      return
    }

    if (value.startsWith(BUSINESS_VALUE_PREFIX)) {
      const businessOption = options.find((option) => option.value === value)
      onBusinessSelect((businessOption?.data as Business | undefined) ?? null)
      onHouseholdSelect(null)
      onContactSelect(null)
      return
    }
  }

  const value = selectedContact
    ? getOptionValue("contact", selectedContact.id)
    : selectedHousehold
      ? getOptionValue("household", selectedHousehold.id)
      : selectedBusiness
        ? getOptionValue("business", selectedBusiness.id)
        : ""

  const isSearching =
    (allowContacts && isFetchingContacts) ||
    (allowHouseholds && isFetchingHouseholds) ||
    (allowBusinesses && isFetchingBusinesses)

  const label = selectedContact
    ? selectedContact.person?.full_name || "Unnamed contact"
    : selectedHousehold
      ? selectedHousehold.name || "Unnamed household"
      : selectedBusiness
        ? selectedBusiness.name || "Unnamed business"
        : "-"
  const hasSelection = !!(selectedContact || selectedHousehold || selectedBusiness)

  return (
    <BadgeComboboxSelect<RelationshipOptionData>
      id="relationship-selector"
      value={value}
      onValueChange={handleValueChange}
      options={options}
      disabled={disabled}
      isSearching={isSearching}
      onSearch={setSearchQuery}
      className={className}
      placeholder="Search records"
      triggerVariant={variant}
    >
      {variant === "field" ? (
        <span className={cn("truncate", hasSelection ? "text-foreground" : "text-muted-foreground")}>
          {hasSelection ? label : "Select related record"}
        </span>
      ) : (
        <InputLabel
          label={label}
          description="Relationship"
          className={cn(hasSelection ? "text-foreground" : "text-muted-foreground", "max-w-36")}
        />
      )}
    </BadgeComboboxSelect>
  )
}
